import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Box, Typography, Paper, CircularProgress, Alert, Button, Link, TextField,
} from '@mui/material';
import { verifyEmail, resendVerification } from '../api/auth';

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

export const VerifyEmail: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [resendEmail, setResendEmail] = useState('');

  const verify = useMutation({ mutationFn: (t: string) => verifyEmail(t) });
  const resend = useMutation({ mutationFn: (e: string) => resendVerification(e) });

  useEffect(() => {
    if (token) verify.mutate(token);
  }, [token]);

  if (!token) {
    return (
      <Centered>
        <Typography variant="h3" sx={{ color: NAVY, mb: 2 }}>Enlace inválido</Typography>
        <Typography sx={{ color: 'text.secondary' }}>El enlace no contiene un token.</Typography>
      </Centered>
    );
  }

  if (verify.isPending) {
    return (
      <Centered>
        <CircularProgress sx={{ color: NAVY }} />
      </Centered>
    );
  }

  if (verify.isSuccess) {
    return (
      <Centered>
        <Typography variant="h3" sx={{ color: NAVY, mb: 2 }}>Cuenta verificada</Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>Ya puedes iniciar sesión.</Typography>
        <Button component={RouterLink} to="/login" variant="contained" sx={{ bgcolor: BLUE }}>
          Ir al inicio de sesión
        </Button>
      </Centered>
    );
  }

  // verify.isError -> show resend flow
  return (
    <Centered>
      <Typography variant="h3" sx={{ color: NAVY, mb: 2 }}>Enlace expirado</Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        El enlace ya no es válido. Ingresa tu correo para reenviar el enlace de verificación.
      </Typography>

      {resend.isSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Si la cuenta existe, te enviamos un nuevo enlace.
        </Alert>
      )}

      <TextField
        fullWidth type="email" label="Correo" value={resendEmail}
        onChange={(e) => setResendEmail(e.target.value)}
        sx={{ mb: 2 }}
      />
      <Button
        fullWidth variant="contained"
        disabled={!resendEmail || resend.isPending}
        onClick={() => resend.mutate(resendEmail)}
        sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0052a3' } }}>
        {resend.isPending ? <CircularProgress size={20} color="inherit" /> : 'Reenviar enlace'}
      </Button>

      <Link component={RouterLink} to="/login" sx={{ display: 'block', mt: 2, color: BLUE }}>
        Volver al inicio de sesión
      </Link>
    </Centered>
  );
};

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ maxWidth: 480, mx: 'auto', mt: { xs: 4, sm: 8 }, px: 3 }}>
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
      {children}
    </Paper>
  </Box>
);
